/**
 * SoundManager — procedural audio via Web Audio API
 * All sounds are synthesized at runtime (no audio files).
 *
 * Browser autoplay policy: AudioContext must be created/resumed inside
 * a user-gesture handler. Call initialize() on the first user click.
 */

import { Quality } from '@/types';

const STORAGE_KEY_MUTED = 'sound_muted';
const STORAGE_KEY_VOLUME = 'sound_volume';

class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private _muted = false;
  private _volume = 0.5;
  private initialized = false;

  constructor() {
    // Restore persisted prefs
    try {
      const savedMuted = localStorage.getItem(STORAGE_KEY_MUTED);
      if (savedMuted !== null) this._muted = savedMuted === 'true';
      const savedVol = localStorage.getItem(STORAGE_KEY_VOLUME);
      if (savedVol !== null) this._volume = Math.max(0, Math.min(1, Number(savedVol)));
    } catch {
      // localStorage unavailable — use defaults
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────

  /**
   * Create (or resume) the AudioContext. Must be called inside a
   * user-gesture handler the very first time.
   */
  initialize(): void {
    if (this.initialized && this.ctx) {
      if (this.ctx.state === 'suspended') {
        this.ctx.resume();
      }
      return;
    }

    try {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.connect(this.ctx.destination);
      this.applyVolume();
      this.initialized = true;
      console.log('🔊 SoundManager initialized');
    } catch (e) {
      console.warn('🔇 Web Audio API unavailable:', e);
    }
  }

  // ── Volume / Mute ─────────────────────────────────────────

  setVolume(volume: number): void {
    this._volume = Math.max(0, Math.min(1, volume));
    this.applyVolume();
    try {
      localStorage.setItem(STORAGE_KEY_VOLUME, String(this._volume));
    } catch { /* noop */ }
  }

  getVolume(): number {
    return this._volume;
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    this.applyVolume();
    try {
      localStorage.setItem(STORAGE_KEY_MUTED, String(muted));
    } catch { /* noop */ }
  }

  isMuted(): boolean {
    return this._muted;
  }

  toggleMute(): boolean {
    this.setMuted(!this._muted);
    return this._muted;
  }

  private applyVolume(): void {
    if (!this.masterGain) return;
    this.masterGain.gain.value = this._muted ? 0 : this._volume;
  }

  // ── Sound Primitives ──────────────────────────────────────

  /**
   * Create an oscillator connected to the master gain.
   * Returns { osc, gain } so caller can shape the envelope further.
   */
  private createTone(
    type: OscillatorType,
    frequency: number,
    startTime: number,
    duration: number,
    peakGain = 0.3,
  ): { osc: OscillatorNode; gain: GainNode } | null {
    if (!this.ctx || !this.masterGain) return null;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, startTime);

    // Simple envelope: quick attack, sustain, quick release
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(peakGain, startTime + 0.005);
    gain.gain.setValueAtTime(peakGain, startTime + duration * 0.7);
    gain.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(startTime);
    osc.stop(startTime + duration + 0.05);

    return { osc, gain };
  }

  /**
   * Create a noise burst (white noise through bandpass filter).
   */
  private createNoiseBurst(
    startTime: number,
    duration: number,
    freqStart: number,
    freqEnd: number,
    peakGain = 0.15,
  ): void {
    if (!this.ctx || !this.masterGain) return;

    const bufferSize = Math.ceil(this.ctx.sampleRate * duration);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(freqStart, startTime);
    filter.frequency.exponentialRampToValueAtTime(Math.max(freqEnd, 20), startTime + duration);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(peakGain, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(startTime);
    source.stop(startTime + duration + 0.05);
  }

  // ── Game Sounds ───────────────────────────────────────────

  /** Water splash — short noise burst */
  playCastSplash(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.createNoiseBurst(t, 0.15, 800, 200, 0.12);
  }

  /** Rising chime on successful catch; richer for rare+ */
  playCatchSuccess(quality: Quality): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;

    // Two-note chime: C5 → E5
    this.createTone('sine', 523.25, t, 0.12, 0.25);
    this.createTone('sine', 659.25, t + 0.1, 0.18, 0.25);

    const isRarePlus =
      quality === Quality.RARE ||
      quality === Quality.EPIC ||
      quality === Quality.LEGENDARY ||
      quality === Quality.UNIQUE;

    const isLegendaryPlus =
      quality === Quality.LEGENDARY ||
      quality === Quality.UNIQUE;

    // Rare+: add G5 to make a major chord
    if (isRarePlus) {
      this.createTone('sine', 783.99, t + 0.18, 0.2, 0.25);
    }

    // Legendary+: add sparkle (high sine sweep)
    if (isLegendaryPlus) {
      const sparkle = this.createTone('sine', 2000, t + 0.25, 0.15, 0.15);
      if (sparkle) {
        sparkle.osc.frequency.exponentialRampToValueAtTime(4000, t + 0.4);
      }
    }
  }

  /** Descending tone on miss */
  playCatchMiss(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const tone = this.createTone('sine', 400, t, 0.3, 0.2);
    if (tone) {
      tone.osc.frequency.exponentialRampToValueAtTime(200, t + 0.3);
    }
  }

  /** Cash register click + ching */
  playSellSound(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    // Short click (square wave)
    this.createTone('square', 1000, t, 0.02, 0.1);
    // Ching (sine)
    this.createTone('sine', 1200, t + 0.03, 0.1, 0.2);
  }

  /** Ascending arpeggio: C5 → E5 → G5 → C6 */
  playQuestComplete(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      this.createTone('sine', freq, t + i * 0.09, 0.15, 0.25);
    });
  }

  /** Triumphant chord: C4 + E4 + G4 + C5 simultaneously */
  playLevelUp(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const chord = [261.63, 329.63, 392.0, 523.25];
    chord.forEach((freq) => {
      this.createTone('sine', freq, t, 0.5, 0.15);
    });
  }

  /** Subtle UI click */
  playClick(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.createTone('square', 800, t, 0.015, 0.08);
  }
}

/** Singleton instance */
export const soundManager = new SoundManager();
